<template>
  <!-- Document section for editing the transcripts -->
  <div class="note-view-container flex flex-col flex-grow rounded-lg lg:rounded-none bg-white sm:bg-none ">
    <!-- mobile header -->
    <div class="flex lg:hidden justify-between p-2 bg-[F5F6F8] border-b border-[#E7EBF2]">
      <div class="my-auto">
        <router-link to="/notes" class="btn-primary p-2 pr-3 font-roboto text-xs">
          <div class="flex">
            <span class="material-icons text-3xl leading-3">keyboard_arrow_left</span>
            <span class="my-auto">Back to list</span>
          </div>
        </router-link>
      </div>

      <div class="relative flex flex-col align-middle">
        <CopyButton class="relative  text-neutral my-auto border border-neutral rounded-full"
                    text="Copy Entire Note"
                    :plaintextSubstitute="copyableFullNotePlainText"
                    reverse
                    target="#text-area-rich-note"
                    @click="updateFullNoteCopyField"
                    @copyClick="trackCopy('full note')" />

      </div>
    </div>

    <!-- title section -->
    <div v-if="note" id="note-header-section"
         class="flex flex-row py-6  border-b border-[#E7EBF2] bg-[#FAFBFC]">
      <div class="flex flex-grow px-4 xl:px-7 max-w-[1280px]">
        <div class="flex flex-col flex-grow">
          <!-- title display and editing block -->
          <div :class="['block text-neutral-darker text-lg font-bold transition-[padding]',
                        editingTitle && (titleSaved || note.title.length >= 80 && pendingTitleChange) ? 'pb-5' : 'pb-0']">
            <div class="flex flex-grow w-full pr-4 ">

              <!-- regular title display -->
              <template v-if="!editingTitle">
                <div id="title" class="font-bold break-all text-lg min-h-[28px] sm:min-h-[32px]">
                  {{ note?.title ? note?.title : "Untitled Note" }}
                </div>
                <button id="edit-note-title" class="material-icons text-gray-500 text-xl ml-2 h-7 w-7 rounded-full hover:bg-gray-700 hover:bg-opacity-25 transition-colors"
                        :disabled="isTrialExpired" @click="toggleTitleEditing">
                  edit
                </button>
              </template>

              <!-- editing title text area and mock div -->
              <div v-else id="title-edit-wrapper" class="relative">
                <textarea id="edit-title-input" v-model="note.title" maxlength="80"
                          :style="`height:${titleTarget.height}px; width:${titleTarget.width + 24}px; max-width:${titleTarget.max}px;`"
                          class="title-input break-all w-fit bg-transparent text-lg font-bold h-8
                                rounded-md border-main-alt box-border lg:max-w-[40rem] overflow-clip"
                          @input="trimInput" @blur="onTitleBlur">
                </textarea>
                <!-- title editing feedback -->
                <Transition name="fade">
                  <baseLoading v-if="pendingTitleChange" style="transition-duration:200ms;" class="transform scale-[40%] h-10 absolute -right-16 top-0 -translate-y-1/4" />
                  <div v-else style="transition-duration:200ms;" class="material-icons absolute text-2xl leading-2 -right-9 top-0">
                    check
                  </div>
                </Transition>
                <div v-if="note.title.length >= 80 && pendingTitleChange" class="absolute block bottom-0 translate-y-full">
                  <div class="block leading-normal whitespace-nowrap error-message">
                    Titles can be 80 characters or less
                  </div>
                </div>
                <div v-if="titleSaved" class="block leading-normal whitespace-nowrap text-rare2-dark text-sm absolute bottom-0 translate-y-full">
                  Title Saved!
                </div>
                <!-- an invisible div that fills the same space as the title, it gives the size that the text area should be rendered.
                    It is needed because text-area's don't fill space or grow/shrink/wrap to fit text now a normal div/contenteditable does-->
                <div id="title-target" class="invisible break-all absolute flex text-wrap font-bold text-lg min-h-[28px] sm:min-h-[32px]"
                     :style="titleTarget.max == 0 ? '' : `width:${titleTarget.max}px;`">
                  <div>
                    {{ note.title }}
                  </div>
                </div>
              </div>

            </div>
          </div>
          <div class="block text-neutral-darker font-roboto text-xs">
            <span v-if="note.assistant_generated" class="text-main-light font-bold">Assistant Generated</span>
            <span class="opacity-50">
              Created on {{ noteCreatedOn.date }} at {{ noteCreatedOn.time }}.
              <div v-if="note.last_edited !== 0" class="inline-block">
                last Edited {{ noteEditedOn.date }} {{ noteEditedOn.time }}
              </div>
            </span>

          </div>

        </div>
        <div class="relative hidden lg:flex flex-col align-middle">

          <CopyButton class="relative  text-neutral my-auto border border-neutral rounded-full"
                      text="Copy Entire Note"
                      :plaintextSubstitute="copyableFullNotePlainText"
                      reverse
                      target="#text-area-rich-note" @click="updateFullNoteCopyField" @copyClick="trackCopy('full note')" />

        </div>
      </div>

    </div>

    <div id="document-section" class="flex flex-col flex-grow  px-4 xl:px-7 4xl:max-w-[1280px]">
      <!-- Header links and actions -->
      <div v-if="isTrialExpired" class="bg-error-lighter border border-error-light text-neutral-darker rounded-md p-2 text-center font-bold mt-4">
        Your trial has ended! While editing is currently unavailable,
        you can still access <br /> your notes.
        To resume editing, add billing information to your account. &nbsp;
        <RouterLink v-if="isUserBillingManager" class="text-sm mx-auto text-center inline-block" to="/subscription">
          Activate Now!
        </RouterLink>
      </div>
      <!-- simultaneous editing warning -->
      <div v-if="simuEditingWarning" class="flex w-full justify-center mt-4">
        <span class="material-symbols-outlined text-error">
          error
        </span>
        <span class="pl-2 font-bold font-roboto">
          Another {{ isUserScribe ? "scribe" : "user" }} is currently editing this note
        </span>
      </div>


      <!-- template section and note action container -->
      <div v-if="note"
           class="flex sm:flex-row flex-col-reverse xl:flex-row sm:justify-between mt-4 mb-2 sm:my-4 xl:my-6">
        <!-- note template selection dropdown -->
        <div class="flex flex-row-reverse xl:flex-row">
          <div v-if="note.state != 0 && note.state != 5"
               v-click-outside="() => { showTemplateOptions = false; }"
               class="flex relative border border-neutral-light rounded-md my-auto w-full"
               @click.stop>
            <button id="template-selection-collapse" class="flex w-full sm:w-40 relative border-r border-neutral-light"
                    @click.stop="toggleTemplateSelection">
              <span class="inline-block my-auto pl-4 text-neutral">
                {{ prettyTemplateName(tempSelectedTemplateType) }}
              </span>

              <div class="flex absolute right-0 top-1/2 -translate-y-1/2 ">
                <span :class="['material-icons text-neutral-light my-auto transition-[transform] duration-150',
                               showTemplateOptions ? '' : 'flip-x']">
                  keyboard_arrow_down
                </span>
              </div>
            </button>
            <!-- regen button -->
            <button id="template-regen-button"
                    class="w-10 h-10 block rounded-r-md p-2 transition-all duration-150 cursor-default hover:bg-pastel-blue-light hover:bg-opacity-25"
                    :disabled="tempSelectedTemplateType === selectedTemplateType"
                    @click="navSelectedTemplate">
              <div class="flex justify-center m-auto">
                <template v-if="!tempSelectedTemplateType">
                </template>
                <template v-else-if="tempSelectedTemplateType !== selectedTemplateType">
                  <span class="text-main-alt">
                    <!-- arrow_circle_right -->
                    <svg id="Layer_1" xmlns="http://www.w3.org/2000/svg" data-name="Layer 1" viewBox="0 0 24 24" width="22" height="22" fill="rgb(3 134 183)"><path d="M12,0C5.383,0,0,5.383,0,12s5.383,12,12,12,12-5.383,12-12S18.617,0,12,0Zm0,21c-4.962,0-9-4.038-9-9S7.038,3,12,3s9,4.038,9,9-4.038,9-9,9Zm5.768-9.552c.308,.304,.308,.8,0,1.104l-4.243,4.188c-.566,.559-1.525,.158-1.525-.638v-2.602H7.5c-.829,0-1.5-.671-1.5-1.5s.671-1.5,1.5-1.5h4.5v-2.602c0-.795,.959-1.197,1.525-.638l4.243,4.188Z" /></svg>
                  </span>
                </template>
                <template v-else>
                  <!-- regenerate -->
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M20 9.99958C20 15.514 15.5142 20 10 20C7.87083 20 5.815 19.3216 4.11167 18.0791L3.055 19.1358C2.55083 19.64 1.68417 19.2775 1.68083 18.5616L1.66583 14.9665C1.66417 14.5223 2.0225 14.1648 2.46583 14.1664L6.06083 14.1814C6.77667 14.1848 7.13917 15.0515 6.635 15.5556L5.91083 16.2798C7.12083 17.069 8.53583 17.4999 10 17.4999C14.1358 17.4999 17.5 14.1348 17.5 9.99958C17.5 9.30872 18.06 8.74953 18.75 8.74953C19.44 8.74953 20 9.30872 20 9.99958ZM2.5 9.99958C2.5 5.86441 5.865 2.49927 10 2.49927C11.4742 2.49927 12.8858 2.92429 14.0942 3.71432L13.3642 4.44435C12.86 4.94854 13.2225 5.81524 13.9383 5.81858L17.5333 5.83358C17.9767 5.83524 18.335 5.47689 18.3333 5.03354L18.3183 1.43839C18.3158 0.72253 17.4483 0.360015 16.9442 0.864203L15.8825 1.92591C14.1817 0.682528 12.1425 1.16092e-07 9.99917 1.16092e-07C4.48583 -0.000833252 0 4.48519 0 9.99958C0 10.6904 0.559167 11.2496 1.25 11.2496C1.94083 11.2496 2.5 10.6904 2.5 9.99958ZM13.8683 9.18622L13.6792 9.29705C13.7225 9.5254 13.75 9.75957 13.75 10.0004C13.75 10.2413 13.7225 10.4754 13.6792 10.7038L13.8683 10.8146C14.4642 11.163 14.665 11.928 14.3167 12.5247C14.0842 12.923 13.6658 13.1439 13.2367 13.1439C13.0217 13.1439 12.805 13.0889 12.6067 12.973L12.4042 12.8547C12.0642 13.1422 11.6767 13.3689 11.25 13.5206V13.7506C11.25 14.4414 10.6908 15.0006 10 15.0006C9.30917 15.0006 8.75 14.4414 8.75 13.7506V13.5206C8.3225 13.3681 7.93583 13.1414 7.59583 12.8547L7.39333 12.973C7.195 13.0889 6.9775 13.1439 6.76417 13.1439C6.33417 13.1439 5.91583 12.9222 5.68417 12.5247C5.33583 11.9288 5.53667 11.163 6.1325 10.8146L6.32167 10.7038C6.27833 10.4754 6.25083 10.2413 6.25083 10.0004C6.25083 9.75957 6.27833 9.5254 6.32167 9.29705L6.1325 9.18622C5.53667 8.83787 5.33583 8.07284 5.68417 7.47615C6.03167 6.87945 6.7975 6.67861 7.39417 7.02779L7.59667 7.14613C7.93667 6.85862 8.32417 6.63194 8.75083 6.48027V6.25026C8.75083 5.5594 9.31 5.00021 10.0008 5.00021C10.6917 5.00021 11.2508 5.5594 11.2508 6.25026V6.48027C11.6783 6.63278 12.065 6.85945 12.405 7.14613L12.6075 7.02779C13.2033 6.67945 13.9675 6.87862 14.3175 7.47615C14.6658 8.072 14.4642 8.83787 13.8683 9.18622ZM10 8.74953C9.31083 8.74953 8.75 9.31039 8.75 9.99958C8.75 10.6888 9.31083 11.2496 10 11.2496C10.6892 11.2496 11.25 10.6888 11.25 9.99958C11.25 9.31039 10.6892 8.74953 10 8.74953Z" fill="#C4C4C4" />
                  </svg>
                </template>
              </div>
            </button>

            <!-- dropdown body -->
            <transition name="fade">
              <div v-if="showTemplateOptions" class="default-fade-duration w-full absolute z-10 -bottom-1 flex flex-col bg-white rounded-md translate-y-full drop-shadow-md">
                <button v-for="name in availableTemplates" :id="`select-template-button-${name}`"
                        :key="name"
                        :class="['flex justify-between transition-colors duration-150 text-left py-3 pl-4 pr-2',
                                 tempSelectedTemplateType === name ? 'bg-rare2-lighter bg-opacity-50' : 'hover:bg-pastel-blue-light hover:bg-opacity-25']"
                        @click="selectTemplate(name)">
                  <span class="text-neutral ">{{ prettyTemplateName(name) }}</span>
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path opacity="0.6" d="M8.33333 18.75C8.33333 19.44 7.77333 20 7.08333 20H4.58333C2.05667 20 0 17.9433 0 15.4167V4.58333C0 2.05667 2.05667 0 4.58333 0H10.4167C12.9433 0 15 2.05667 15 4.58333V6.25C15 6.94 14.44 7.5 13.75 7.5C13.06 7.5 12.5 6.94 12.5 6.25V4.58333C12.5 3.43417 11.5658 2.5 10.4167 2.5H4.58333C3.43417 2.5 2.5 3.43417 2.5 4.58333V15.4167C2.5 16.5658 3.43417 17.5 4.58333 17.5H7.08333C7.77333 17.5 8.33333 18.06 8.33333 18.75ZM5.41667 4.16667C4.72667 4.16667 4.16667 4.72667 4.16667 5.41667C4.16667 6.10667 4.72667 6.66667 5.41667 6.66667H9.58333C10.2733 6.66667 10.8333 6.10667 10.8333 5.41667C10.8333 4.72667 10.2733 4.16667 9.58333 4.16667H5.41667ZM7.91667 8.33333H5.41667C4.72667 8.33333 4.16667 8.89333 4.16667 9.58333C4.16667 10.2733 4.72667 10.8333 5.41667 10.8333H7.91667C8.60667 10.8333 9.16667 10.2733 9.16667 9.58333C9.16667 8.89333 8.60667 8.33333 7.91667 8.33333ZM6.25 15C6.94 15 7.5 14.44 7.5 13.75C7.5 13.06 6.94 12.5 6.25 12.5H5.41667C4.72667 12.5 4.16667 13.06 4.16667 13.75C4.16667 14.44 4.72667 15 5.41667 15H6.25ZM20 14.5833C20 17.575 17.575 20 14.5833 20C11.5917 20 9.16667 17.575 9.16667 14.5833C9.16667 11.5917 11.5917 9.16667 14.5833 9.16667C17.575 9.16667 20 11.5917 20 14.5833ZM17.9167 12.9167C17.4342 12.4233 16.6433 12.4142 16.1492 12.8967L14.1642 14.8358L13.4342 14.1333C12.9367 13.6542 12.145 13.67 11.6667 14.1675C11.1883 14.665 11.2033 15.4558 11.7008 15.935L12.6942 16.8908C13.1017 17.2983 13.6375 17.5008 14.1708 17.5008C14.7008 17.5008 15.23 17.3008 15.63 16.9L17.8967 14.685C18.3908 14.2025 18.3992 13.4108 17.9167 12.9167Z" fill="#1B9F81" />
                  </svg>
                </button>

              </div>
            </transition>
          </div>
        </div>

        <!-- Notes Action container. draft submit, and action dropdown -->
        <div class="flex justify-end mb-4 my-auto sm:mb-auto w-full">
          <template v-if="note === undefined"></template>
          <div v-else-if="note.state == 0" class="flex">
            <button id="submit-draft-note-button" class="bg-main-alt rounded-[20px] text-white w-44 py-2 font-roboto text-sm text-center font-semibold"
                    :disabled="closingNote" @click="closeNote">
              Submit Draft
            </button>
          </div>
          <div v-else-if="note.state == 5" class="flex">
            <button id="processing-button" class="flex justify-center bg-secondary-lighter rounded-[20px] text-white w-44 py-2 font-roboto text-sm text-center font-semibold cursor-pointer"
            >
              Processing
              <div class="relative h-4 w-4">
                <div class="h-4 w-4 absolute left-2/3 top-1/4 -translate-x-1/2 -translate-y-1/2">
                  <BaseLoading class="translate scale-[0.25] " />
                </div>
              </div>
            </button>
          </div>
          <!-- Notes Action Dropdown -->
          <div v-else v-click-outside="(e) => { toggleNoteActions(e, false); }" class="flex w-full sm:w-auto relative" @click.stop>
            <button id="selected-note-action-dropdown"
                    :class="[' text-white w-full sm:w-44 bg-rare2-dark py-2 font-roboto text-sm text-center font-semibold border-r border-white hover:bg-rare2',
                             'transition-all', showNoteActions ? 'rounded-tl-[20px]' : 'rounded-l-[20px]']"
                    @click="takeNoteAction">
              {{ selectedNoteAction }}
            </button>
            <button id="note-action-dropdown-collapsable"
                    :class="['flex items-center py-2 text-white bg-rare2-dark font-medium transition-all hover:bg-rare2',
                             showNoteActions ? 'rounded-tr-[20px]' : ' rounded-r-[20px]']"
                    :disabled="possibleNoteActions.length === 0"
                    @click.stop="toggleNoteActions">
              <span :class="['material-icons px-3 transform transition-transform my',
                             showNoteActions ? '' : 'quarter-z']"> keyboard_arrow_down</span>
            </button>
            <Transition name="fade">
              <div v-if="showNoteActions"
                   class="absolute z-50 w-full bg-white bottom-0 right-0 rounded-b-md  drop-shadow-md
                      transform translate-y-full  default-fade-duration">
                <div class="flex-flex-col ">
                  <button v-for="action in possibleNoteActions"
                          :id="`dropdown-option-note-action-${action.toLowerCase().replaceAll(' ', '-')}`"
                          :key="action"
                          :class="['flex w-full transition-colors text-neutral-alt p-4',
                                   action == selectedNoteAction ? 'text-semibold bg-pastel-green-light' : 'hover:bg-pastel-blue-light hover:bg-opacity-25']"

                          @click="selectDefaultNoteAction(action)">
                    {{ action }}
                  </button>

                </div>
              </div>
            </Transition>
          </div>
        </div>


      </div>
      <!-- main note container -->
      <div class="flex flex-col flex-grow xl:flex-row">
        <!-- note field container -->
        <div class="flex flex-col w-full h-full flex-grow space-y-3">
          <!-- original transcript drop down -->
          <div v-if="selectedTemplateType !== 'no_template'"
               id="original-transcript"
               class="flex flex-col transcript-container-shadow border-[#7B99A7] border">
            <div id="original-transcript-collapse" class="flex justify-between items-center py- px-4 bg-[#7B99A7] border-2 border-[#7B99A7] text-white" role="button"
                 @click.stop="toggleShowTranscript">
              <div class="font-extrabold text-xs">
                <div class="flex">
                  <span :class="['text-sm font-bold my-auto mr-2 transform transition-all',
                                 showTranscript ? '' : 'quarter-z']">
                    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="6" viewBox="0 0 10 6" fill="none">
                      <path d="M1.41421 0C0.523309 0 0.077142 1.07714 0.707107 1.70711L4.29289 5.29289C4.68342 5.68342 5.31658 5.68342 5.70711 5.29289L9.29289 1.70711C9.92286 1.07714 9.47669 0 8.58579 0L1.41421 0Z" fill="white" />
                    </svg>
                  </span>
                  Transcript
                </div>
              </div>
              <CopyButton class="font-bold text-xs"
                          target="#text-area-original-transcript"
                          @click.stop
                          @copyClick="trackCopy('transcript')" />
            </div>
            <Transition name="expand" appear :duration="300"
                        @before-enter="setEnterLeaveHeight"
                        @before-leave="setTransitionHeight"
                        @leave="setEnterLeaveHeight"
                        @enter="setTransitionHeight"
                        @after-leave="transitionDone"
                        @after-enter="transitionDone">

              <div v-if="showTranscript"
                   :class="['flex h-full w-full overflow-hidden transition-all font-normal']">
                <div class="block h-fit p-6 bg-white w-full">
                  {{ originalTranscript }}
                </div>
              </div>
            </transition>
          </div>

          <!-- No Template Editable Section -->
          <template v-if="note && note.id && selectedTemplateType === 'no_template'">
            <div id="editable-transcript" class="flex flex-col transcript-container-shadow border-[#7B99A7] border">
              <div class="flex justify-between items-center py- px-4 bg-[#7B99A7] border-2 border-[#7B99A7] text-white">
                <div class="font-extrabold text-xs">
                  <div class="flex">
                    Text View
                  </div>
                </div>
                <CopyButton
                  class="font-bold text-xs"
                  target="#pm-editor-editable-transcript"
                  :plaintextSubstitute="copyableDraftPlainText('no_template')"
                  @copyClick="trackCopy('no template')" />
              </div>
              <div class="section-text-area">
                <RichTextField :id="'editable-transcript'"
                               :tabIndex="1"
                               :isPlaying="isPlaying"
                               :editable="enableEditing"
                               :transcriptString="note.edited_transcript_string"
                               :noteId="note.id"
                               :noteState="note.state"
                               @focus="() => focusedField = true"
                               @blur="focusedField = false"
                               @textChangeEvent="checkState"
                               @transcriptEditedEvent="(text) => handleTranscriptChange(text)" />
              </div>
              <div class="relative opacity-0 overflow-hidden h-0 w-full">
                <textarea id="text-area-rich-note" v-model="note.edited_transcript_string" class="absolute">
                </textarea>
                <textarea id="text-area-edited-transcript" v-model="note.edited_transcript_string" class="absolute">
              </textarea>
              </div>
            </div>
          </template>
          <template v-else-if="note && note.id && note.note_templates.length !== 0">

            <template v-for="(field, index) in templateKey"
                      :key="field.field">
              <div v-if="settingsShow(field.field)" :id="`${field.field}-editor`" :class="`template-container-${field.field} transcript-container-shadow rounded-[2px]`">
                <div class="transcript-container-header capitalize" role="button"
                     @click.stop="() => { field.show = !field.show }">
                  <div class="flex">
                    <span :class="['text-sm font-bold my-auto mr-2 transform transition-all',
                                   field.show ? '' : 'quarter-z']">
                      <svg xmlns="http://www.w3.org/2000/svg" width="10" height="6" viewBox="0 0 10 6" fill="none">
                        <path d="M1.41421 0C0.523309 0 0.077142 1.07714 0.707107 1.70711L4.29289 5.29289C4.68342 5.68342 5.31658 5.68342 5.70711 5.29289L9.29289 1.70711C9.92286 1.07714 9.47669 0 8.58579 0L1.41421 0Z" fill="white" />
                      </svg>
                    </span>
                    <span class="capitalize">{{ field.field }} </span>
                  </div>
                  <div class="flex items-center self-end text-xs">
                    <div class="tooltip-basic relative mr-2">
                      <div class="tooltip-text text-center whitespace-nowrap">
                        This section is accurate
                      </div>
                      <button class="material-symbols-outlined thumb up"
                              :data-thumbed="selectedTemplateFields.ratings[field.field]"
                              @click.stop="(e) => upThumb(e, field.field)">
                        thumb_up
                      </button>
                    </div>
                    <div class="tooltip-basic relative mr-2">
                      <div class="tooltip-text text-center  whitespace-nowrap">
                        This section is inaccurate
                      </div>
                      <button class="material-symbols-outlined thumb down" :data-thumbed="selectedTemplateFields.ratings[field.field]"
                              @click.stop="(e) => downThumb(e, field.field)">thumb_down</button>
                    </div>

                    <CopyButton :target="`#copyable-rich-${field.field} > :first-child`"
                                :plaintextSubstitute="copyableDraftPlainText(field)"
                                @click.stop="copyRichField(field)"
                                @copyClick="trackCopy(field.field)">
                    </CopyButton>
                  </div>

                </div>
                <Transition name="expand" appear :duration="300"
                            @before-enter="setEnterLeaveHeight"
                            @before-leave="setTransitionHeight"
                            @leave="setEnterLeaveHeight"
                            @enter="setTransitionHeight"
                            @after-leave="transitionDone"
                            @after-enter="transitionDone">
                  <div v-if="field.show" class="block transition-all overflow-y-hidden duration-[300ms]">
                    <div class="section-text-area">
                      <RichTextField :id="field.field"
                                     :tabIndex="index + 1"
                                     :isPlaying="isPlaying"
                                     :editable="enableEditing"
                                     :transcriptString="selectedTemplateFields[field.field]"
                                     :noteId="note.id"
                                     :noteState="note.state"
                                     @focus="() => focusedField = true"
                                     @blur="focusedField = false"
                                     @textChangeEvent="checkState"
                                     @transcriptEditedEvent="(text) => handleTemplateChange(field.field, text)" />
                    </div>
                  </div>
                </transition>
                <div class="relative opacity-0 overflow-hidden h-0 w-full">
                  <div :id="`copyable-rich-${field.field}`"
                       class="absolute bg-white bg-opacity-0">
                  </div>
                </div>
              </div>
            </template>
            <!-- client communication -->
            <div id="client-comm-editor" :class="`template-container-client-comm transcript-container-shadow rounded-[2px]`">
              <div :class="['transcript-container-header capitalize transition-[border]',
                            showComm? 'border-b-2' : 'delay-150 border-b-0']" role="button"
                   @click.stop="toggleClientCommunication">
                <div class="flex">
                  <span :class="['text-[#7B99A7] text-sm font-bold my-auto mr-2 transform transition-all',
                                 showComm ? '' : 'quarter-z']">
                    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="6" viewBox="0 0 10 6" fill="none">
                      <path d="M1.41421 0C0.523309 0 0.077142 1.07714 0.707107 1.70711L4.29289 5.29289C4.68342 5.68342 5.31658 5.68342 5.70711 5.29289L9.29289 1.70711C9.92286 1.07714 9.47669 0 8.58579 0L1.41421 0Z" fill="#7B99A7" />
                    </svg>
                  </span>
                  <span class="text-[#7B99A7] capitalize"> Client Communication </span>
                </div>
                <div class="flex items-center self-end text-xs">

                  <template v-if="selectedTemplate.client_communication === undefined">
                    <button class="transition-colors hover:bg-opacity-20 hover:bg-main-alt flex p-2 my-auto align-middle rounded-full"
                            :disabled="generatingComm || !enableEditing"
                            @click.stop="generateClientCommunication">
                      <span class="text-main-alt text-xs font-bold my-auto leading-1 pr-2">Generate</span>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
                        <path d="M16 8.49965C16 13.1869 12.4113 17 8 17C6.29667 17 4.652 16.4234 3.28933 15.3672L2.444 16.2654C2.04067 16.694 1.34733 16.3859 1.34467 15.7774L1.33267 12.7215C1.33133 12.3439 1.618 12.04 1.97267 12.0415L4.84867 12.0542C5.42133 12.057 5.71133 12.7937 5.308 13.2223L4.72867 13.8379C5.69667 14.5087 6.82867 14.8749 8 14.8749C11.3087 14.8749 14 12.0145 14 8.49965C14 7.91241 14.448 7.4371 15 7.4371C15.552 7.4371 16 7.91241 16 8.49965ZM2 8.49965C2 4.98475 4.692 2.12438 8 2.12438C9.17933 2.12438 10.3087 2.48565 11.2753 3.15717L10.6913 3.7777C10.288 4.20626 10.578 4.94296 11.1507 4.94579L14.0267 4.95854C14.3813 4.95996 14.668 4.65536 14.6667 4.27851L14.6547 1.22263C14.6527 0.614151 13.9587 0.306013 13.5553 0.734572L12.706 1.63703C11.3453 0.580149 9.714 9.86785e-08 7.99933 9.86785e-08C3.58867 -0.000708264 0 3.81241 0 8.49965C0 9.08688 0.447333 9.56219 1 9.56219C1.55267 9.56219 2 9.08688 2 8.49965ZM11.0947 7.80828L10.9433 7.9025C10.978 8.09659 11 8.29564 11 8.50036C11 8.70507 10.978 8.90412 10.9433 9.09821L11.0947 9.19242C11.5713 9.48852 11.732 10.1388 11.4533 10.646C11.2673 10.9846 10.9327 11.1723 10.5893 11.1723C10.4173 11.1723 10.244 11.1255 10.0853 11.0271L9.92333 10.9265C9.65133 11.1709 9.34133 11.3636 9 11.4925V11.688C9 12.2752 8.55267 12.7505 8 12.7505C7.44733 12.7505 7 12.2752 7 11.688V11.4925C6.658 11.3628 6.34867 11.1702 6.07667 10.9265L5.91467 11.0271C5.756 11.1255 5.582 11.1723 5.41133 11.1723C5.06733 11.1723 4.73267 10.9839 4.54733 10.646C4.26867 10.1395 4.42933 9.48852 4.906 9.19242L5.05733 9.09821C5.02267 8.90412 5.00067 8.70507 5.00067 8.50036C5.00067 8.29564 5.02267 8.09659 5.05733 7.9025L4.906 7.80828C4.42933 7.51219 4.26867 6.86191 4.54733 6.35472C4.82533 5.84754 5.438 5.67682 5.91533 5.97362L6.07733 6.07421C6.34933 5.82983 6.65933 5.63715 7.00067 5.50823V5.31272C7.00067 4.72549 7.448 4.25018 8.00067 4.25018C8.55333 4.25018 9.00067 4.72549 9.00067 5.31272V5.50823C9.34267 5.63786 9.652 5.83054 9.924 6.07421L10.086 5.97362C10.5627 5.67753 11.174 5.84683 11.454 6.35472C11.7327 6.8612 11.5713 7.51219 11.0947 7.80828ZM8 7.4371C7.44867 7.4371 7 7.91383 7 8.49965C7 9.08546 7.44867 9.56219 8 9.56219C8.55133 9.56219 9 9.08546 9 8.49965C9 7.91383 8.55133 7.4371 8 7.4371Z" fill="#0386B7" />
                      </svg>
                    </button>
                  </template>
                  <template v-else>
                    <CopyButton class="text-main-alt" :target="`#copyable-rich-client_communication > :first-child`"
                                :plaintextSubstitute="copyableClientCommPlainText()"
                                @click.stop="copyRichField({field:'client_communication'})"
                                @copyClick="trackCopy('client_communication')">
                    </CopyButton>
                  </template>
                </div>

              </div>
              <template v-if="selectedTemplate.client_communication !== undefined">
                <Transition name="expand" appear :duration="300"
                            @before-enter="setEnterLeaveHeight"
                            @before-leave="setTransitionHeight"
                            @leave="setEnterLeaveHeight"
                            @enter="setTransitionHeight"
                            @after-leave="transitionDone"
                            @after-enter="transitionDone">
                  <div v-if="showComm" class="block transition-all overflow-y-hidden duration-[300ms]">
                    <div class="section-text-area">
                      <RichTextField :id="'client-communication'"
                                     :tabIndex="5"
                                     :isPlaying="isPlaying"
                                     :editable="enableEditing"
                                     :transcriptString="selectedTemplate.client_communication"
                                     :noteId="note.id"
                                     :noteState="note.state"
                                     @focus="() => focusedField = true"
                                     @blur="focusedField = false"
                                     @textChangeEvent="checkState"
                                     @transcriptEditedEvent=" handleClientCommunicationChange" />
                    </div>
                  </div>
                </transition>
              </template>
              <div class="relative opacity-0 overflow-hidden h-0 w-full">
                <div :id="`copyable-rich-client_communication`"
                     class="absolute bg-white bg-opacity-0">
                </div>
              </div>
            </div>
            <!-- this needs to not be hidden in some sort of way that isn't display:none;  theres a few redundant ways that this is hidden.
              Text areas are position:absolute for browser reasons I guess. the text will not select and copy without it -->
            <div class="relative opacity-0 overflow-hidden h-0 w-full">
              <div id="text-area-rich-note"
                   class="absolute bg-white bg-opacity-0">
              </div>
              <textarea id="text-area-full-note" v-model="copyableNote" class="absolute">
                </textarea>
              <textarea id="text-area-original-transcript" v-model="originalTranscript" class="absolute">
              </textarea>
            </div>
          </template>

        </div>
      </div>
    </div>
  </div>

  <!-- media bar -->
  <div v-if="note && !note.assistant_generated" class="sticky bottom-0 z-[1] mt-auto">
    <div id="media-bar" class="media-bar-container w-full sm:py-2">
      <div class="py-2 px-4 xl:px-7">
        <div :class="['media-bar-wrapper relative sm:static flex flex-wrap sm:justify-between', canPlay ? '' : 'disable-contents']">
          <!-- 5f play/pause 5b-->
          <div id="playback" class="flex my-auto flex-grow sm:flex-grow-0 basis-full sm:basis-0 sm:ml-0 sm:mr-4 order-1 select-none whitespace-nowrap">
            <div class="flex h-6 mx-auto">
              <button class="playback-icon inline-flex mr-1 px-[0.125rem]" :disabled="!canPlay ?? undefined"
                      title="ctrl + J" @click="changeCurrTime(-5)">
                <div class="m-auto">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path opacity="0.7" d="M6.83102 0C10.1746 0 13.0005 2.23263 14 5.32L12.2958 5.89474C11.5408 3.54421 9.37648 1.84211 6.83102 1.84211C5.42167 1.84211 4.14895 2.37263 3.14946 3.22737L5.03338 5.15789H0V0L1.86954 1.91579C3.19979 0.736842 4.92553 0 6.83102 0ZM4.31433 6.63158H8.62866V8.10526H5.75244V9.57895H7.19055C7.9887 9.57895 8.62866 10.2421 8.62866 11.0526V12.5263C8.62866 13.3442 7.9887 14 7.19055 14H4.31433V12.5263H7.19055V11.0526H4.31433V6.63158Z" fill="#52BAE2" />
                  </svg>
                </div>
              </button>
              <button v-if="!isPlaying" class="playback-icon inline my-auto text-white" :disabled="!canPlay ?? undefined"
                      title="ctrl + K" @click="togglePlayback">
                <i class="block material-icons my-auto ">
                  play_arrow
                </i>
              </button>
              <button v-else class="playback-icon inline my-auto text-white" :disabled="!canPlay ?? undefined"
                      title="ctrl + J" @click="togglePlayback">
                <i class="material-icons my-auto">
                  pause
                </i>
              </button>
              <button class="playback-icon inline-flex ml-1 px-[0.125rem]" :disabled="!canPlay ?? undefined"
                      title="ctrl + L" @click="changeCurrTime(5)">
                <div class="m-auto">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path opacity="0.7" d="M7.16898 0C9.07447 0 10.8002 0.736842 12.1305 1.91579L14 0V5.15789H8.96662L10.8505 3.22737C9.85105 2.37263 8.57833 1.84211 7.16898 1.84211C4.62352 1.84211 2.45917 3.54421 1.70416 5.89474L0 5.32C0.999486 2.23263 3.82537 0 7.16898 0ZM5.37134 6.63158H9.68567V8.10526H6.80945V9.57895H8.24756C9.04571 9.57895 9.68567 10.2421 9.68567 11.0526V12.5263C9.68567 13.3442 9.04571 14 8.24756 14H5.37134V12.5263H8.24756V11.0526H5.37134V6.63158Z" fill="#52BAE2" />
                  </svg>
                </div>
              </button>
            </div>
          </div>
          <!-- playback timeline -->
          <div id="timeline" class="flex flex-grow order-3 sm:order-2">
            <div class="my-auto mr-4 w-12 text-white">
              {{ currTimeMinutes }}
            </div>
            <div id="playback-bar" :class="['relative my-auto rounded-md w-full h-1 bg-main-alt', canPlay ? 'cursor-pointer' : '']"
                 @mousemove="seekClip"
                 @mousedown="seekClip">
              <div
                :class="['flex relative h-full bg-main-lighter rounded-md ', playbackBarDragging ? 'select-none' : 'transition-all ease-linear duration-300']"
                :style="`width:${audioConfig && audioConfig.duration > 0 ?
                  (audioConfig.currTime / audioConfig.duration) * 100 : 0}%;`">
                <div id="playback-knob"
                     class="bg-main-alt h-4 w-4 rounded-full shadow-md cursor-pointer
                            absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2">
                </div>
              </div>
            </div>
            <div class="my-auto ml-4 w-12 text-white">
              {{ durationMinutes }}
            </div>
          </div>
          <!-- volume controls speed -->
          <div class="flex absolute sm:static right-0 sm:left-0 my-auto sm:ml-4 basis-1/5 sm:basis-0 order-2 sm-order-3">
            <div class="flex mx-auto">
              <BaseIconDropdown class="text-xl h-6 w-6"
                                menuX="right-3/4 translate-x-1/2 transform"
                                menuY="-top-2 transform -translate-y-full"
                                :icon="volumeIcon"
                                iconColor="main-lighter"
                                noBorder
                                :disabled="!canPlay">
                <div id="slider-container" class="mx-4">
                  <div id="volume-slider" class="bg-gray-200 mx-auto w-28 h-1 rounded-md cursor-pointer"
                       @mousedown="volumeChange"
                       @mouseup="volumeChange">
                    <div id="slider" class="relative bg-rare-alt h-full rounded-md"
                         :style="`width:${(audioConfig?.volume / 1) * 100}%;`">
                      <div id="volume-knob"
                           :class="['bg-white h-4 w-4 rounded-full border shadow-md cursor-pointer absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2',
                                    volumeDragging ? 'select-none' : '']" />
                    </div>
                  </div>
                </div>
              </BaseIconDropdown>
              <!-- playback rate controls -->
              <BaseIconDropdown icon="speed"
                                class="text-xl h-6 w-6 ml-2"
                                iconColor="main-lighter"
                                menuX="right-0"
                                menuY="-top-2 transform -translate-y-full"
                                noBorder
                                :disabled="!canPlay">
                <div class="rate-dropdown flex flex-col text-xs">
                  <button v-for="index in 7" :key="index"
                          :class="audioConfig?.playbackRate === (index + 1) * 0.25 ?
                            'bg-gray-300 bg-opacity-50' : ''"
                          :data-rate="audioConfig?.playbackRate === (index + 1) * 0.25"
                          @click="changePlaybackRate((index + 1) * 0.25)">
                    {{ ((index + 1) * 0.25) === 1 ? "Normal" : ((index + 1) * 0.25) }}
                  </button>
                </div>
              </BaseIconDropdown>
            </div>
          </div>
          <div v-if="noteState === 'Draft'" class="absolute flex w-full h-full top-0 left-0 z-10 bg-main-darker">
            <div class="flex text-white mx-auto text-sm font-bold text-center rounded-md">
              <div class="my-auto">
                Draft audio cannot be played back. Please submit the note to review the audio.
              </div>
            </div>
          </div>
          <div v-else-if="noAudio" class="absolute flex w-full h-full top-0  left-0 bg-main-darker">
            <div class="flex text-white mx-auto text-sm font-bold text-center rounded-md">
              <div class="my-auto">
                Audio cannot be played back. There may be no audio available for this note.
              </div>
            </div>
          </div>
          <BaseLoading v-else-if="!canPlay" style="transition-duration:200ms;" class="z-50 transform scale-[50%] -translate-x-1/2 -translate-y-1/2 left-1/2 top-7 absolute" />
        </div>
      </div>

    </div>
  </div>

  <BaseModal id="create-issue-modal" title="Note Needs Attention" :toDisplay="showAttentionModal" @toggleEvent="toggleAttentionModal">
    <div class="max-w-lg lg:w-128">
      <div class="px-4 sm:px-7 pt-7 font-roboto">
        <div class="py-4 text-gray-500 text-base font-medium">
          Describe the issue this note has so that the author can <br class="hidden sm:block">
          resolve the problem and you can complete your review.
        </div>

        <div :class="['pt-4 pb-3 flex flex-col transition-[height] duration-300 ease-in-out',
                      selectedProblem == 999 ? 'h-[10.5rem]' : 'h-[4.5rem]']">
          <BaseSelectList :list="problemsList" initialValue="1" @select="(val) => selectedProblem = val" />
          <BaseInput v-if="selectedProblem == 999" v-model="otherProblem"
                     class="pt-6" label="Other:" placeholderText="Please specify" />
        </div>
        <BaseTextArea v-model="problemDescription"
                      class="h-56 text-sm font-normal" placeholderText="Describe the issue"
        />
      </div>
      <div class="flex modal-end px-7 py-4 ">
        <div class="flex justify-end flex-grow">
          <button class="cancel-button" @click="toggleAttentionModal">
            CANCEL
          </button>
          <button
            :disabled="submittedNeedsAttn || problemDescription === '' || (selectedProblem == 999 && otherProblem === '')"
            class="bg-main-alt hover:bg-main text-white rounded-lg text-xs py-2 px-3 transition-colors"
            @click="SubmitIssue">
            SUBMIT ISSUE
          </button>
        </div>
      </div>
    </div>
  </BaseModal>

</template>

<script src="./BladedNoteView.js">
</script>
<style src="./BladedNoteView.css" scoped></style>
<style></style>